/** @format */

import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Alert } from "react-bootstrap";
import { ReactComponent as ExclamationTriangle } from "bootstrap-icons/icons/exclamation-triangle.svg";
import { ReactComponent as ExclamationCircle } from "bootstrap-icons/icons/exclamation-circle.svg";
import { QualificationId, Subject } from "../../../types";
import { useLocale } from "../../../i18n/hooks";
import { Trans, useTranslation } from "react-i18next";
import { qualIdToFull } from "../../../utils/functions";
import "./UploadConfirmationModal.scss";

type KeyData = {
  code?: string;
  subject?: Partial<Subject>;
  qualification?: string;
  type?: string;
};

type ValidatedKeyData = {
  code: { valid: boolean; reason?: string };
  subject: { valid: boolean; reason?: string };
  qualification: { valid: boolean; reason?: string };
  type: { valid: boolean; reason?: string };
};

interface UploadConfirmationModalProps {
  show: boolean;
  onHide: () => void;
  onAccept: () => void;
  keyData: KeyData;
}

const UploadConfirmationModal: React.FC<UploadConfirmationModalProps> = ({
  show,
  keyData,
  onHide,
  onAccept,
}) => {
  const [validated, setValidated] = useState<ValidatedKeyData>();
  const locale = useLocale();
  const { t: _ } = useTranslation("translation", { keyPrefix: "uploadConfirmationModal" });
  const { t: typeTranslation } = useTranslation();

  const checkCode = useCallback(() => {
    const code = keyData?.code;
    if (!code) {
      return { valid: false, reason: _("noCode") };
    } else {
      const projectCodeRegex =
        /^([a-z][a-z][a-z]?)(\d{2})-(\d{2})_([1-9][0-9]?)-([1-9][0-9]?|0)/g;

      const match = projectCodeRegex.exec(code);
      if (match) {
        const firstYear = parseInt(match[2], 10);
        const secondYear = parseInt(match[3], 10);
        if (secondYear === firstYear + 1) {
          return { valid: true };
        } else {
          return {
            valid: false,
            reason: _("dateRangeIncorrect"),
          };
        }
      } else {
        // Check if it's a valid start code
        const validStart = /^([a-z][a-z][a-z]?)/g.test(code);
        if (!validStart) {
          return { valid: false, reason: _("incorrectStartCode") };
        }

        // Check if date is incorrect
        const validDate = /^([a-z][a-z][a-z]?)(\d{2})-(\d{2})/g.test(code);
        if (!validDate) {
          return { valid: false, reason: _("invalidDate") };
        }

        return {
          valid: false,
          reason:
            _("incorrectFormat"),
        };
      }
    }
  }, [keyData, _]);

  const checkExists = useCallback(
    (key: "subject" | "qualification" | "type") => {
      if (!!keyData?.[key]) {
        return { valid: true };
      } else {
        return { valid: false, reason: _(`noKeyProvided`, { key }) };
      }
    },
    [keyData, _],
  );

  useEffect(() => {
    setValidated({
      code: checkCode(),
      subject: checkExists("subject"),
      qualification: checkExists("qualification"),
      type: checkExists("type"),
    });

    return () => {
      setValidated(undefined);
    };
  }, [checkCode, checkExists]);

  return (
    <Modal centered show={show} onHide={onHide}>
      <Modal.Header>{_("reviewDetails")}</Modal.Header>
      <Modal.Body>
        <Alert
          variant="warning"
          className="d-flex flex-row gap-3 align-items-center"
        >
          <ExclamationTriangle className="flex-shrink-0" />
          <p className="m-0">
            <Trans
              i18nKey="uploadConfirmationModal.confirmationWarning"
              // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content
              components={{ strong: <strong />}}
            />
          </p>
        </Alert>
        {/** DISPLAY CODE | TYPE | SUBJECT | QUALIFICATION */}
        <table className="w-100 important-field-overview">
          <tbody>
            <tr>
              <td>{_("code")}:</td>
              <td className="d-flex flex-row gap-2 align-items-center">
                {validated?.code.valid === false && (
                  <ExclamationCircle className="text-danger" />
                )}{" "}
                <p className="m-0">
                  {validated?.code.valid === false ? <strong>{keyData?.code}</strong> : keyData?.code}
                </p>
              </td>
            </tr>
            <tr>
              <td>{_("type")}:</td>
              <td className="d-flex flex-row gap-2 align-items-center">
                {validated?.type.valid === false && (
                  <ExclamationCircle className="text-danger" />
                )}{" "}
                <p className="m-0">
                  {validated?.type.valid === false ? <strong>{keyData.type && typeTranslation(keyData.type)}</strong> : keyData.type && typeTranslation(keyData.type)}
                  <br />
                  <code>{keyData.type?.toLowerCase()}</code>
                </p>
              </td>
            </tr>
            <tr>
              <td>{_("subject")}:</td>
              <td className="d-flex flex-row gap-2 align-items-center">
                {validated?.subject.valid === false && (
                  <ExclamationCircle className="text-danger" />
                )}{" "}
                <p className="m-0">
                  { validated?.subject.valid === false ? <strong>{keyData?.subject?.title?.[locale]}</strong> : keyData?.subject?.title?.[locale]}
                  <br />
                  <code>{keyData?.subject?.id}</code>
                </p>
              </td>
            </tr>
            <tr>
              <td>{_("qualification")}:</td>
              <td className="d-flex flex-row gap-2 align-items-center">
                {validated?.qualification.valid === false && (
                  <ExclamationCircle className="text-danger" />
                )}{" "}
                <p className="m-0">
                  { validated?.qualification.valid === false ? <strong>{qualIdToFull(keyData.qualification as QualificationId)}</strong> : qualIdToFull(keyData.qualification as QualificationId)}
                  <br />
                  <code>{keyData.qualification}</code>
                </p>
              </td>
            </tr>
          </tbody>
        </table>
        {validated &&
          Object.keys(validated).filter(
            (key) => !validated[key as keyof ValidatedKeyData].valid,
          ).length > 0 && (
            <Alert variant="danger" className="mt-3">
              <p className="mb-1">
                {_("problemsWithSections")}
              </p>
              <ul className="mb-0">
                {validated?.code.valid === false && (
                  <li>{_("code")}: {validated.code.reason}</li>
                )}
                {validated?.subject.valid === false && (
                  <li>{_("subject")}: {validated.subject.reason}</li>
                )}
                {validated?.qualification.valid === false && (
                  <li>{_("qualification")}: {validated.qualification.reason}</li>
                )}
                {validated?.type.valid === false && (
                  <li>{_("type")}: {validated.type.reason}</li>
                )}
              </ul>
            </Alert>
          )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-editing" onClick={onHide}>
          {_("cancel")}
        </Button>
        <Button variant="editing" onClick={onAccept}>
          {_("create")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UploadConfirmationModal;
